import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import * as authService from '../services/authService'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [roleReady, setRoleReady] = useState(false)
  
  // Nuevos estados para usuarios del portal
  const [isPortalUser, setIsPortalUser] = useState(false)
  const [isOperator, setIsOperator] = useState(false)
  const [assignedAccounts, setAssignedAccounts] = useState([])
  const [portalRole, setPortalRole] = useState('principal') // 'principal' puede transferir, 'lector' solo consulta
  
  const navigate = useNavigate()
  const location = useLocation()
  const expirationTimerRef = useRef(null)

  useEffect(() => {
    checkAuth()
    
    // Verificar auth cada 30 minutos (para tokens de 1 hora de CREDENCIAL)
    const authCheckInterval = setInterval(() => {
      if (window.location.pathname !== '/login') {
        checkAuth()
      }
    }, 1800000) // 30 minutos
    
    return () => {
      if (expirationTimerRef.current) {
        clearInterval(expirationTimerRef.current)
      }
      clearInterval(authCheckInterval)
    }
  }, [])

  // Verificar expiración del token cada segundo
  useEffect(() => {
    if (isAuthenticated && tokenExpiresAt) {
      expirationTimerRef.current = setInterval(() => {
        const now = Date.now()
        if (now >= tokenExpiresAt) {
          console.log('⏰ Token expirado - Solicitando re-login')
          handleTokenExpiration()
        }
      }, 1000)

      return () => {
        if (expirationTimerRef.current) {
          clearInterval(expirationTimerRef.current)
        }
      }
    }
  }, [isAuthenticated, tokenExpiresAt])

  // Portal al que pertenece este frontend. Si el backend devuelve un user
  // cuyo `portal_tag` no es 'zento', lo expulsamos. Operadores tradicionales
  // (urbana_4315, api-sasteupsa-prd, BIND, etc.) no tienen `portal_tag` y
  // pueden loguearse en cualquier portal, así que los dejamos pasar.
  const FRONTEND_PORTAL = 'flash'

  // Usuarios "globales" que pueden entrar a todos los portales aunque
  // tengan portal_tag distinto. Defensa adicional al guardrail.
  const GLOBAL_USERS = new Set(['urbana_4315'])

  const isPortalAllowed = (userPortalTag, username) => {
    if (username && GLOBAL_USERS.has(String(username).toLowerCase())) return true
    if (!userPortalTag) return true // backend viejo / operadores / BIND
    return String(userPortalTag).toLowerCase() === FRONTEND_PORTAL
  }

  const checkAuth = async () => {
    try {
      setRoleReady(false)
      const result = await authService.checkAuth()

      // Guardrail: si el backend ya nos avisa el portal_tag del user y no
      // coincide con este frontend, cerramos sesión.
      if (result.authenticated && !isPortalAllowed(result.portal_tag, result.username)) {
        console.warn('⚠️ Usuario de otro portal detectado en checkAuth, forzando logout')
        clearLocalStorage()
        setIsAuthenticated(false)
        try {
          await authService.logout()
        } catch (_) {}
        localStorage.setItem(
          'login_blocked_message',
          'Usuario o contraseña incorrectos'
        )
        navigate('/login')
        return
      }

      setIsAuthenticated(result.authenticated)
      
      if (result.authenticated) {
        const savedExpiration = localStorage.getItem('token_expires_at')
        const savedRole = localStorage.getItem('user_role')
        const savedIsPortalUser = localStorage.getItem('is_portal_user') === 'true'
        const savedIsOperator = localStorage.getItem('is_operator') === 'true'
        const savedPortalRole = localStorage.getItem('portal_role') || 'principal'
        
        // Parsear y verificar que sea un array (fix para datos viejos)
        let savedAssignedAccounts = []
        try {
          const parsed = JSON.parse(localStorage.getItem('assigned_accounts') || '[]')
          savedAssignedAccounts = Array.isArray(parsed) ? parsed : []
        } catch (e) {
          savedAssignedAccounts = []
        }
        
        if (savedExpiration) {
          setTokenExpiresAt(parseInt(savedExpiration))
        }
        if (savedRole) {
          setUserRole(savedRole)
          setRoleReady(true)
        } else {
          // Si no hay rol guardado pero está autenticado, forzar re-login
          // Esto evita que se asuma un rol incorrecto
          console.warn('⚠️ Usuario autenticado pero sin rol guardado - forzando re-login')
          clearLocalStorage()
          setIsAuthenticated(false)
          navigate('/login', { 
            state: { message: 'Sesión incompleta. Por favor, inicia sesión nuevamente.' }
          })
          return
        }
        
        // Restaurar datos del portal
        setIsPortalUser(savedIsPortalUser)
        setIsOperator(savedIsOperator)
        setPortalRole(savedPortalRole)
        
        // Si es usuario del portal, cargar CVUs desde el backend (más seguro que localStorage)
        if (savedIsPortalUser) {
          try {
            console.log('📋 Recargando CVUs asignados...')
            const accountsResult = await authService.getMyAccounts()
            const accounts = accountsResult.accounts || []
            setAssignedAccounts(accounts)
            localStorage.setItem('assigned_accounts', JSON.stringify(accounts))
            console.log(`📋 CVUs recargados: ${accounts.length}`)
          } catch (e) {
            console.error('Error recargando CVUs:', e)
            setAssignedAccounts(savedAssignedAccounts)
          }
        } else {
          setAssignedAccounts(savedAssignedAccounts)
        }
        
      } else {
        setRoleReady(false)
        const currentPath = window.location.pathname
        if (currentPath !== '/login') {
          localStorage.setItem('return_path', currentPath)
          clearLocalStorage()
          navigate('/login', { 
            state: { 
              message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
              returnPath: currentPath
            } 
          })
        }
      }
    } catch (error) {
      setIsAuthenticated(false)
      setRoleReady(false)
      const currentPath = window.location.pathname
      if (currentPath !== '/login') {
        navigate('/login')
      }
    } finally {
      setLoading(false)
    }
  }

  const clearLocalStorage = () => {
    localStorage.removeItem('token_expires_at')
    localStorage.removeItem('user_role')
    localStorage.removeItem('username')
    localStorage.removeItem('is_portal_user')
    localStorage.removeItem('is_operator')
    localStorage.removeItem('assigned_accounts')
    localStorage.removeItem('portal_role')
  }

  const handleTokenExpiration = async () => {
    const currentPath = location.pathname
    
    // Guardar solo el return_path, el resto se limpia
    const savedReturnPath = currentPath
    
    if (expirationTimerRef.current) {
      clearInterval(expirationTimerRef.current)
    }
    
    // Limpiar TODO el estado local PRIMERO (crítico para evitar cambio de rol)
    clearLocalStorage()
    setIsAuthenticated(false)
    setTokenExpiresAt(null)
    setUserRole(null)
    setRoleReady(false)
    setIsPortalUser(false)
    setIsOperator(false)
    setAssignedAccounts([])
    setPortalRole('principal')
    
    // Ahora sí guardar el return path (después de limpiar)
    localStorage.setItem('return_path', savedReturnPath)
    
    // Logout del backend
    try {
      await authService.logout()
    } catch (e) {
      // Ignorar errores de logout
    }
    
    navigate('/login', { 
      state: { 
        message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
        returnPath: savedReturnPath
      } 
    })
  }

  const login = async (username, password, extra = {}) => {
    try {
      const result = await authService.login(username, password, extra)

      if (result?.mfaRequired) {
        return { success: false, mfaRequired: true }
      }

      // Guardrail: si el backend nos avisa el portal_tag del user y no
      // coincide con este frontend, hacemos logout inmediato y devolvemos
      // un mensaje GENÉRICO (no filtramos que el user existe en otro portal).
      if (!isPortalAllowed(result.portal_tag, username)) {
        try { await authService.logout() } catch (_) {}
        return {
          success: false,
          error: 'Usuario o contraseña incorrectos'
        }
      }

      const expiresInMs = result.expires_in * 1000
      const expiresAt = Date.now() + expiresInMs
      const role = result.role || 'BIND'
      const portalUser = result.isPortalUser || false
      const operator = result.isOperator || false
      const userPortalRole = result.portalRole || 'principal'
      
      // Guardar en localStorage (sin CVUs)
      localStorage.setItem('token_expires_at', expiresAt.toString())
      localStorage.setItem('user_role', role)
      localStorage.setItem('username', username)
      localStorage.setItem('is_portal_user', portalUser.toString())
      localStorage.setItem('is_operator', operator.toString())
      localStorage.setItem('portal_role', userPortalRole)
      
      // Setear estados básicos
      setUserRole(role)
      setRoleReady(true)
      setTokenExpiresAt(expiresAt)
      setIsAuthenticated(true)
      setIsPortalUser(portalUser)
      setIsOperator(operator)
      setPortalRole(userPortalRole)
      
      console.log(`✅ Token recibido - Expira en ${result.expires_in} segundos`)
      console.log(`👤 Rol de usuario: ${role}`)
      console.log(`🏢 Es usuario del portal: ${portalUser}`)
      console.log(`⚙️ Es operador: ${operator}`)
      console.log(`🔐 Rol del portal: ${userPortalRole}`)
      
      // Si es usuario del portal, cargar CVUs desde el backend
      let accounts = []
      if (portalUser) {
        try {
          console.log('📋 Cargando CVUs asignados...')
          const accountsResult = await authService.getMyAccounts()
          accounts = accountsResult.accounts || []
          console.log(`📋 CVUs cargados: ${accounts.length}`)
        } catch (e) {
          console.error('Error cargando CVUs:', e)
          accounts = []
        }
      }
      
      // Guardar CVUs en localStorage y estado
      localStorage.setItem('assigned_accounts', JSON.stringify(accounts))
      setAssignedAccounts(accounts)
      
      await new Promise(resolve => setTimeout(resolve, 200))
      
      const returnPath = localStorage.getItem('return_path')
      if (localStorage.getItem('flash_market') === 'CL') {
        navigate('/clp')
      } else if (returnPath && returnPath !== '/login') {
        localStorage.removeItem('return_path')
        navigate(returnPath)
      } else {
        navigate(portalUser ? '/mis-cvus' : '/accounts')
      }
      
      return { success: true, role }
    } catch (error) {
      // Si el backend rechazó por portal_tag distinto, mostramos un mensaje
      // genérico de credenciales para no filtrar que el user existe en otro
      // portal.
      if (
        error.response?.status === 403 &&
        error.response?.data?.code === 'PORTAL_MISMATCH'
      ) {
        return { success: false, error: 'Usuario o contraseña incorrectos' }
      }

      return { 
        success: false, 
        error: error.response?.data?.error || 'Error de autenticación' 
      }
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      if (expirationTimerRef.current) {
        clearInterval(expirationTimerRef.current)
      }
      
      setIsAuthenticated(false)
      setTokenExpiresAt(null)
      setUserRole(null)
      setRoleReady(false)
      setIsPortalUser(false)
      setIsOperator(false)
      setAssignedAccounts([])
      setPortalRole('principal')
      clearLocalStorage()
      localStorage.removeItem('return_path')
      localStorage.removeItem('flash_market')
      navigate('/login')
    }
  }

  const username = localStorage.getItem('username')

  const value = {
    isAuthenticated,
    loading,
    login,
    logout,
    tokenExpiresAt,
    userRole,
    roleReady,
    username,
    // Nuevos valores para portal
    isPortalUser,
    isOperator,
    assignedAccounts,
    portalRole // 'principal' puede transferir, 'lector' solo consulta
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
